import { useState } from "react";
import { useGraphicDownloads, useDeleteGraphicDownload } from "@/hooks/useGraphicDownloads";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Image, Calendar, FileText, Trash2, ShieldCheck } from "lucide-react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const formatLabel = (formatType: string): string => {
  switch (formatType) {
    case "post":
      return "Post (4:5)";
    case "story":
      return "Story (9:16)";
    case "compact":
      return "Kompaktowy";
    default:
      return formatType;
  }
};

export const GraphicDownloadsTab = () => {
  const { data: downloads, isLoading, error } = useGraphicDownloads(true);
  const deleteGraphic = useDeleteGraphicDownload();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; name: string } | null>(null);

  const openDeleteDialog = (id: string, name: string) => {
    setItemToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await deleteGraphic.mutateAsync(itemToDelete.id);
      toast.success(`Usunięto wpis dla "${itemToDelete.name}"`);
    } catch {
      toast.error("Błąd usuwania wpisu");
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-destructive">
          <p>Błąd ładowania historii pobrań</p>
        </CardContent>
      </Card>
    );
  }

  if (!downloads || downloads.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Image className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
          <p className="font-medium">Brak zapisanych pobrań grafik</p>
          <p className="text-sm">Tutaj pojawią się grafiki pobierane przez użytkowników</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-lg">Historia pobrań grafik</h2>
          <Badge variant="outline">{downloads.length} pobrań</Badge>
        </div>

        <div className="space-y-3">
          {downloads.map((download) => (
            <Card key={download.id} className="overflow-hidden">
              <CardHeader className="py-3 px-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">
                      {download.shelter_name}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1 text-xs">
                      <Calendar className="w-3 h-3" />
                      {format(new Date(download.downloaded_at), "d MMM yyyy, HH:mm", { locale: pl })}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {download.is_admin_download && (
                      <Badge variant="outline" className="gap-1 text-muted-foreground">
                        <ShieldCheck className="w-3 h-3" />
                        Admin
                      </Badge>
                    )}
                    <Badge variant="outline">
                      {formatLabel(download.format)}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(download.id, download.shelter_name)}
                      className="h-12 w-12 sm:h-8 sm:w-8 text-muted-foreground hover:text-destructive touch-manipulation active:bg-destructive/10"
                    >
                      <Trash2 className="h-6 w-6 sm:h-4 sm:w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {download.needs_text && (
                <CardContent className="py-3 px-4 pt-0">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      Treść potrzeb:
                    </p>
                    <p className="text-sm whitespace-pre-wrap line-clamp-4">
                      {download.needs_text}
                    </p>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć wpis?</AlertDialogTitle>
            <AlertDialogDescription>
              Czy na pewno chcesz usunąć wpis o pobraniu grafiki dla "{itemToDelete?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="touch-manipulation">Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 touch-manipulation"
            >
              Usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
